import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

function PostListItem({ post, index }) {
  return (
    <article
      className="grid grid-cols-12 gap-6 py-10 border-b border-[#D9D2C7] fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`post-item-${post.slug}`}
    >
      <div className="col-span-12 md:col-span-3">
        <p className="overline" data-testid={`post-type-${post.slug}`}>
          {post.type === "poem" ? "Poem" : "Story"} · {post.reading_minutes} min
        </p>
        <p className="font-ui text-xs text-[#8B827A] mt-2">
          {post.published_at
            ? new Date(post.published_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : ""}
        </p>
      </div>
      <div className="col-span-12 md:col-span-9">
        <Link to={`/p/${post.slug}`} className="block group" data-testid={`post-link-${post.slug}`}>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-[#1A1A1A] group-hover:text-[#C45543] transition-colors">
            {post.title}
          </h2>
        </Link>
        {post.excerpt && (
          <p className="mt-3 text-[#5C554E] italic max-w-2xl leading-relaxed">{post.excerpt}</p>
        )}
        {post.tags?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="tag-pill" data-testid={`post-tag-${post.slug}-${t}`}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export default function Home({ filterType }) {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const activeTag = params.get("tag") || "";
  const q = params.get("q") || "";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (filterType) query.set("type", filterType);
        if (activeTag) query.set("tag", activeTag);
        if (q) query.set("q", q);
        const [postRes, tagRes] = await Promise.all([
          api.get(`/posts?${query.toString()}`),
          api.get(`/tags`),
        ]);
        setPosts(postRes.data);
        setTags(tagRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterType, activeTag, q]);

  const heading = useMemo(() => {
    if (filterType === "story") return "Stories";
    if (filterType === "poem") return "Poems";
    return "The Reading Room";
  }, [filterType]);

  const subheading = useMemo(() => {
    if (filterType === "story")
      return "Narratives, fragments, and quiet little fictions.";
    if (filterType === "poem") return "Verses to be read slowly, ideally aloud.";
    return "A small, considered collection of stories and poems — published as they are written.";
  }, [filterType]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <div className="paper-grain" data-testid="home-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8 fade-up">
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#1A1A1A]">
              {heading}
              <span className="text-[#C45543]">.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[#5C554E] italic text-lg leading-relaxed">
              {subheading}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 fade-up fade-up-delay-1">
            <div className="border-t border-[#1A1A1A] pt-4">
              <p className="overline mb-3">Search</p>
              <input
                type="text"
                placeholder="A word, a title, a thought…"
                defaultValue={q}
                onKeyDown={(e) => {
                  if (e.key === "Enter") updateParam("q", e.currentTarget.value);
                }}
                className="input-literary"
                data-testid="home-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tag filters */}
      {tags.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-4">
          <div className="flex flex-wrap items-center gap-2 border-t border-b border-[#D9D2C7] py-4">
            <span className="overline mr-3">Threads</span>
            <button
              onClick={() => updateParam("tag", "")}
              className="tag-pill"
              data-active={!activeTag}
              data-testid="tag-filter-all"
            >
              All
            </button>
            {tags.map((t) => (
              <button
                key={t.name}
                onClick={() => updateParam("tag", t.name)}
                className="tag-pill"
                data-active={activeTag === t.name}
                data-testid={`tag-filter-${t.name}`}
              >
                {t.name} <span className="ml-2 opacity-60">{t.count}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Post list */}
      <section
        className="max-w-6xl mx-auto px-6 lg:px-10 pb-20"
        data-testid="post-list"
      >
        {loading ? (
          <p className="font-display italic text-[#5C554E] py-16">Turning pages…</p>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center" data-testid="empty-state">
            {q || activeTag ? (
              <>
                <p className="font-display italic text-2xl text-[#5C554E]">
                  Nothing here matches{q ? ` “${q}”` : ""}
                  {activeTag ? ` in “${activeTag}”` : ""}.
                </p>
                <button
                  onClick={() => setParams(new URLSearchParams())}
                  className="btn-link mt-4"
                  data-testid="empty-state-clear"
                >
                  Clear filters
                </button>
              </>
            ) : filterType ? (
              <>
                <p className="font-display italic text-2xl text-[#5C554E]">
                  No {filterType === "poem" ? "poems" : "stories"} have been
                  published yet.
                </p>
                <p className="mt-2 overline">
                  Drafts in the Desk won&rsquo;t appear here until published.
                </p>
              </>
            ) : (
              <>
                <p className="font-display italic text-2xl text-[#5C554E]">
                  The pages are blank — for now.
                </p>
                <p className="mt-2 overline">
                  Drafts in the Desk won&rsquo;t appear here until published.
                </p>
              </>
            )}
          </div>
        ) : (
          posts.map((post, i) => <PostListItem key={post.id} post={post} index={i} />)
        )}
      </section>
    </div>
  );
}
